import logger from '../../logger';
import athenaConfig from '../config';
import { TerminalError } from './errors';
import { APP_TYPES, OIE_ENROLLED_FACTOR_CONFIG, PROVIDER_FACTOR_TYPE_APP_MAPPING } from '../constants';

const { oieEnabled, classic, oktaUrl } = athenaConfig;

export const $ = global.jQueryCourage;

const AUTH_CONTAINER_SELECTOR = '.auth-content-inner';

const getAppState = () => global.oktaSignIn.router.appState;

export function getOktaUserId() {
  return global.oktaSignIn?.getUser()?.id;
}

export function getClientId(requestContext: any): string | undefined {
  try {
    const requestContextClientId = requestContext?.authentication?.client?.id;
    if (requestContextClientId) {
      return requestContextClientId;
    }
    // When appEmbeddedLink url (E.g. https://identity.athenahealth.com/home/athena_cornerstonesamlapp_1/0oabvwil1bS7qsc9e297/alnbvwv8v8M17DSpc297)
    // is loaded for classic okta env, clientId is absent in request context target and is present as issuer id
    const requestContextIssuer = requestContext?.authentication?.issuer;
    if (requestContextIssuer) {
      const { id, uri } = requestContextIssuer;
      if (id && uri) {
        const issuerUriParts = uri.split('/');
        const authServerId = issuerUriParts[issuerUriParts.length - 1];
        // Make sure we aren't returning auth server id as client id
        if (authServerId !== id) {
          return id;
        }
      }
    }
  }
  catch (e) {
    // eslint-disable-next-line no-empty
  }
}

export function getOIEStateHandle() {
  return getAppState().get('idx')?.context?.stateHandle;
}

export function getStateToken() {
  return getAppState()?.get('lastAuthResponse')?.stateToken;
}

export function showFormErrors(error: any) {
  if (oieEnabled) {
    showFormErrorsOie(error);
  }
  else {
    showFormErrorsClassic(error);
  }
}

function showFormErrorsOie(error: any) {
  const { controller } = global.oktaSignIn.router;
  const { formView } = controller;
  const { model, form } = formView;
  controller.showFormErrors(model, error, form);
}

function showFormErrorsClassic(error: any) {
  const model = global.oktaSignIn.router.controller.model;
  model.trigger('error', model, error.xhr);
  model.trigger('setTransactionError', error);
}

export function getRawAppName(requestContext: any): string | undefined {
  return (
    requestContext?.target?.label ||
    global.oktaSignIn?.router?.appState?.get('app')?.label ||
    global.oktaSignIn?.router?.appState?.get('targetLabel')
  );
}

export function getAppClientId(requestContext: any) {
  return requestContext?.target?.clientId;
}

function getFormElements(excludeInfoContainer: boolean = false) {
  const selectors = [
    jQueryCourage('.o-form-fieldset-container'),
    jQueryCourage('.o-form-button-bar'),
    jQueryCourage('.okta-form-title'),
    jQueryCourage('.ion-messages-container'),
    jQueryCourage('#athena-o-form-head'),
    jQueryCourage('#athena-footer'),
    jQueryCourage('#athena-forgot-password-link'),
    jQueryCourage('#athena-o-form-terms-container'),
    jQueryCourage('#athena-additional-links'),
    jQueryCourage('.okta-form-subtitle'),
  ];
  if (!excludeInfoContainer) {
    selectors.push(jQueryCourage('#athena-o-form-info-container'));
  }
  return selectors;
}

export function hideWidgetForm(excludeInfoContainer: boolean = false) {
  const errorContainer = document.querySelector('.o-form-error-container');
  if (errorContainer) {
    errorContainer.textContent = '';
  }
  getFormElements(excludeInfoContainer).forEach((element) => {
    if (element) {
      element.hide();
    }
  });
}

export function showWidgetForm() {
  getFormElements().forEach((element) => {
    if (element) {
      element.show();
    }
  });
}

function getSpinnerContainer() {
  const authContainer = document.querySelector('.auth-content') as HTMLElement;
  const spinnerElement = authContainer?.querySelector('.beacon-container') as HTMLElement;
  if (spinnerElement) {
    return spinnerElement;
  }
  else {
    return document.querySelector('.beacon-container') as HTMLElement;
  }
}

export const stopSpinner = () => {
  if (oieEnabled) {
    const container = getSpinnerContainer();
    container?.classList?.remove('beacon-loading');
  }
  else {
    getAppState()?.trigger('loading', false);
    getAppState()?.trigger('removeLoading');
  }
};
export const stopSpinnerAndShowForm = () => {
  try {
    stopSpinner();
    showWidgetForm();
  }
  catch (e) {
    logger.log(e);
  }
};

export const startSpinner = (props?: { forceDisplaySpinner?: boolean }) => {
  if (oieEnabled) {
    const container = getSpinnerContainer();
    container.classList.add('beacon-loading');
    if (props?.forceDisplaySpinner) {
      if (!container.style.transform) {
        container.style.transform = 'scale(1, 1)';
      }
      if (!container.style.textIndent) {
        container.style.textIndent = '1px';
      }
      if (!container.classList.contains('beacon-small')) {
        container.classList.add('beacon-small');
      }
    }
  }
  else {
    setTimeout(() => getAppState()?.trigger('loading', true));
  }
};
export const startSpinnerAndHideForm = () => {
  try {
    startSpinner();
    hideWidgetForm();
  }
  catch (e) {
    logger.log(e);
  }
};

export function getOktaWidgetModel() {
  if (oieEnabled) {
    return global.oktaSignIn.router.controller.formView.model;
  }
  else {
    return global.oktaSignIn.router.controller.model;
  }
}

function getEmailFromUsername(): string {
  const username = getAppState().get('username');
  return username && oktaSignIn.router.settings.transformUsername(username, 'PRIMARY_AUTH');
}

export function getUsername(): string {
  let login;
  if (oieEnabled) {
    login = getAppState().get('user')?.identifier;
  }
  else {
    login = getAppState().get('userEmail') || getEmailFromUsername();
  }
  return login;
}

export function getUserPhoneNumber(): string {
  return getAppState().get('userPhoneNumber');
}

export function getSelectedProvider(): string {
  return global.oktaSignIn.router?.controller?.options?.provider;
}

export function getSelectedFactorType(): string {
  return global.oktaSignIn.router?.controller?.options?.factorType;
}

export function getCountryCallingCode(countryCode: string): string {
  const model = getOktaWidgetModel();
  if (oieEnabled) {
    return '+' + model.derived.phoneCode.fn(countryCode);
  }
  return model.derived.countryCallingCode.fn(countryCode);
}

export function isPrimaryAuthController(controller: string | undefined): boolean {
  return !!(controller && [ 'primary-auth', 'idp-discovery', 'mfa-verify-password' ].includes(controller));
}

export function isPasswordExpiredController(controller: string | undefined): boolean {
  return controller === 'password-expired';
}

export const navigateToPrimaryAuthWithError = (errorMessage: string): void => {
  const router = global.oktaSignIn.router;
  getAppState().set('flashError', new TerminalError(errorMessage));
  router.controller.state.set('navigateDir', 'DIRECTION_BACK');
  router.navigate('signin', { trigger: true });
};

export const isRequestFromSamlClient = (requestContext: any): boolean => {
  return requestContext?.target?.name !== 'oidc_client';
};

export const isRequestFromOidcClient = (): boolean => {
  const requestContext = OktaUtil.getRequestContext();
  return requestContext?.target?.name === 'oidc_client';
};
/**
 * For a smooth render between okta screen and forge styled screens
 * we hide the main content before render and unhide after every customization
 */
export const applyContentHidingForCustomization = () => {
  const originalRender = global.oktaSignIn.router.render.bind(global.oktaSignIn.router);
  global.oktaSignIn.router.render = (...args: any[]) => {
    jQueryCourage(AUTH_CONTAINER_SELECTOR).addClass('hide-element');
    originalRender.apply(global.oktaSignIn.router, args);
  };
};

export function unhideWidget() {
  jQueryCourage(AUTH_CONTAINER_SELECTOR).removeClass('hide-element');
}

export function getAuthenticatorSelectedChannel() {
  return oktaSignIn.router?.controller?.options?.appState?.get('currentAuthenticator')?.contextualData?.selectedChannel;
}

export function enrolledFactorsDuringVerification() {
  return getAppState()?.getRemediationAuthenticationOptions('select-authenticator-authenticate') ?? [];
}

export function enrolledFactorsDuringEnrollment() {
  const appType = settings.appType;
  const enrollments = getAppState()?.get('idx')?.rawIdxState?.authenticatorEnrollments?.value ?? [];
  return enrollments.filter(({ key }: { key: string }) => appType === APP_TYPES.PATIENT ? key in OIE_ENROLLED_FACTOR_CONFIG && key !== 'security_question' : key in OIE_ENROLLED_FACTOR_CONFIG);
}

export function getEnrolledPhoneFactorMethods(): ('sms' | 'call' | 'unknown')[] | undefined {
  const enrolledPhoneFactors = getAppState()?.get('authenticatorEnrollments')?.value?.filter(({ key }: { key: string }) => key === 'phone_number');
  const enrolledPhoneFactorMethods = enrolledPhoneFactors?.map((phoneFactor: { id: string }) => {
    if (phoneFactor.id.startsWith('sms')) {
      return 'sms';
    }
    else if (phoneFactor.id.startsWith('cl')) {
      return 'call';
    }
    else {
      return 'unknown';
    }
  });
  return enrolledPhoneFactorMethods;
}

export function getCurrentAuthenticator() {
  return getAppState()?.get('currentAuthenticator')?.key;
}

export function getCurrentAuthenticatorMethodForPhone() {
  const widgetModel = getOktaWidgetModel();
  return widgetModel?.get('authenticator.methodType');
}

export async function closeOktaSession() {
  try {
    await oktaSignIn.authClient.session.close();
  }
  catch (err) {
    /* empty */
  }
}

export async function onBackToSignIn() {
  if (classic) {
    await closeOktaSession();
    window.location.reload();
  }
  else {
    delete global.oktaSignIn.authClient.options.idx;
  }
}

export async function onLogout() {
  onBackToSignIn();
  await closeOktaSession();
}

export function getAuthenticatorMethodType() {
  return getAppState().get('authenticatorMethodType');
}

export function getAuthServer(requestContext: any) {
  return requestContext?.authentication?.issuer?.id;
}

export function getVanityIssuer(requestContext: any) {
  const authServer = getAuthServer(requestContext);
  return oktaUrl + '/oauth2/' + authServer;
}

export function navigateToTerminalViewWithError(errorKey: string) {
  global.oktaSignIn.router.controller.options.appState.setNonIdxError({
    errorDetails: {
      errorSummary: errorKey,
    },
  });
}

export async function makeRequest(opts: any) {
  return await global.oktaSignIn.authClient.invokeApiMethod(opts);
}

export function getFactorApplicationName() {
  const provider = getSelectedProvider();
  const factorType = getSelectedFactorType();
  const providerFactorTypes = PROVIDER_FACTOR_TYPE_APP_MAPPING[provider];
  if (providerFactorTypes) {
    return providerFactorTypes[factorType];
  }
}

export function getRequestState() {
  return global.OktaUtil?.getRequestContext()?.authentication?.request?.state;
}

export function clearLastIdentifier() {
  global.oktaSignIn.router.appState.set('lastIdentifier', '');
}
